/**
 * What each role sees in the sidebar.
 *
 * Role changes what is PRESENT, not what is greyed out. A viewer has no
 * Approvals item at all — a disabled control only teaches someone what they
 * are missing, and invites them to go looking for a way around it.
 *
 * This is presentation. The backend refuses the work regardless.
 *
 * ONLY ROUTES THAT EXIST APPEAR HERE. A navigation item pointing at an
 * unbuilt route is worse than a missing one: it looks like a feature, and
 * clicking it lands on "page not found", which reads as the product being
 * broken rather than unfinished.
 */

import { hasAtLeast, type Role } from '../types/roles';
import { type AddonKey, type Addons } from '../features/org/useAddons';
import type { NavIconName } from './NavIcon';

export interface NavItem {
  to: string;
  label: string;
  /** Required: a sidebar with icons on most rows reads as unfinished. */
  icon: NavIconName;
  /** Shown when the route is gated; undefined means everyone sees it. */
  visible?: (roles: readonly Role[]) => boolean;
  /**
   * Shown to somebody the role gate would hide it from, when they have work
   * waiting there anyway. An administrator may name ANY active member on an
   * approval step, staff included, and the server lets that person decide it —
   * but the sidebar hid Approvals below Reviewer, so their only way in was the
   * emailed link. This grants nothing: the page and the callable behind it
   * return only what this person may decide, and every admin route stays
   * gated on its role.
   */
  alsoWhenWaiting?: boolean;
  /** Shown only where the organization shows acknowledgement reports. */
  needsAckReports?: boolean;
  /**
   * A paid add-on the item belongs to. Absent from the sidebar entirely when
   * the organization has not turned it on — the same rule as roles: what is
   * present, not what is greyed out.
   */
  addon?: AddonKey;
  /**
   * Other path roots this item owns.
   *
   * For paths that belong to a section but do not sit under its URL: a policy
   * lives at `/policy/:id`, not under `/policies`, yet reading one is being in
   * the policy library.
   */
  covers?: string[];
}

export const NAV: NavItem[] = [
  { to: '/', icon: 'tasks', label: 'Your tasks' },
  // `/policy/:id` and its viewer are the same section under a different root.
  // So is `/search`: the Policies page is where a policy is found, and the
  // separate search page has no item of its own. Its route stays, for the
  // bookmarks and links that point at it, and it reads as part of Policies.
  { to: '/policies', icon: 'policies', label: 'Policies', covers: ['/policy', '/search'] },
  {
    to: '/policies/upload',
    icon: 'upload',
    label: 'Upload',
    visible: (r) => hasAtLeast(r, 'author'),
  },
  {
    to: '/my-approvals',
    icon: 'approvals',
    label: 'Approvals',
    visible: (r) => hasAtLeast(r, 'reviewer'),
    alsoWhenWaiting: true,
  },
  // Add-on modules sit between a person's own work and the admin section:
  // every member reads them, authors and admins act on them.
  { to: '/scope-of-service', icon: 'scope', label: 'Scope of service', addon: 'scopeOfService' },
  { to: '/exceptions', icon: 'exceptions', label: 'Exceptions', addon: 'exceptionManagement' },
  { to: '/competencies', icon: 'competencies', label: 'Competencies', addon: 'competencyEvidence' },
  { to: '/forms', icon: 'forms', label: 'Forms', addon: 'formsAttestation' },
  {
    to: '/admin/approval-inbox',
    icon: 'inbox',
    label: 'Approval inbox',
    visible: (r) => hasAtLeast(r, 'admin'),
  },
  {
    to: '/admin/approval-workflows',
    icon: 'workflows',
    label: 'Workflows',
    visible: (r) => hasAtLeast(r, 'admin'),
  },
  {
    to: '/admin/taxonomy',
    icon: 'taxonomy',
    label: 'Taxonomy',
    visible: (r) => hasAtLeast(r, 'admin'),
  },
  {
    to: '/admin/users',
    icon: 'staff',
    label: 'Staff',
    visible: (r) => hasAtLeast(r, 'admin'),
  },
  // Bringing a whole library in at once: the organization's System Admin only.
  {
    to: '/admin/import',
    icon: 'importLibrary',
    label: 'Import library',
    visible: (r) => r.includes('sysadmin'),
  },
  {
    to: '/admin/acknowledgements',
    icon: 'acknowledgements',
    label: 'Acknowledgements',
    // Not a rank: who may see who has acknowledged is the organization's own
    // setting, defaulting to Administrators. A hospital that opens it to
    // Approvers must get the sidebar item too, or the server serves a page the
    // navigation says does not exist.
    needsAckReports: true,
  },
  {
    to: '/admin/overdue',
    icon: 'overdue',
    label: 'Overdue',
    needsAckReports: true,
  },
  {
    to: '/admin/org-settings',
    icon: 'organization',
    label: 'Organization',
    visible: (r) => hasAtLeast(r, 'admin'),
  },
  {
    to: '/admin/audit',
    icon: 'audit',
    label: 'Audit log',
    visible: (r) => hasAtLeast(r, 'admin'),
  },
  // Taking the whole library out again. Promised in writing by section 6 of
  // the subscription agreement, so it is a visible item rather than something
  // a customer has to ask support for.
  {
    to: '/admin/export',
    icon: 'export',
    label: 'Export',
    visible: (r) => hasAtLeast(r, 'admin'),
  },
  // Each person's own: account, email preferences, and for a system
  // administrator the organization's single sign-on.
  { to: '/settings', icon: 'settings', label: 'Settings' },
  { to: '/help', icon: 'help', label: 'Help' },
];

/**
 * `maySeeAckReports` has no default on purpose.
 *
 * It is the organization's own answer to "may this person see who has
 * acknowledged", which already accounts for their roles, and it defaults to
 * Administrators. A default of `true` here would show the two acknowledgement
 * items to everybody the moment a caller forgot to pass it, and a default of
 * `false` would hide them from administrators for the same reason. So the
 * caller has to say, and there is one caller.
 */
export function visibleNav(
  roles: readonly Role[],
  addons: Addons,
  waiting: boolean,
  maySeeAckReports: boolean,
): NavItem[] {
  return NAV.filter(
    (item) => ((!item.visible || item.visible(roles)) || (item.alsoWhenWaiting && waiting))
      && (!item.addon || addons[item.addon])
      && (!item.needsAckReports || maySeeAckReports),
  );
}

/**
 * Which navigation item the current path belongs to.
 *
 * LONGEST MATCH WINS, and that is the whole point. React Router's NavLink
 * highlights on any prefix, so `/policies/upload` lit up Policies as well as
 * Upload — two items looking selected, which reads as a bug because it is one.
 * Choosing the most specific owner resolves that without anybody maintaining a
 * list of exceptions: Upload owns a longer prefix than Policies, so it wins on
 * its own page and loses everywhere else.
 *
 * Matching is SEGMENT-AWARE. A plain `startsWith` would make `/policy` own
 * `/policies`, which is the same class of bug one level down.
 *
 * Returns the `to` of the owning item, or null when the path belongs to none
 * of them — a page reached from somewhere other than the sidebar, which should
 * leave every item unselected rather than guessing.
 */
export function activeNavPath(pathname: string, items: readonly NavItem[] = NAV): string | null {
  // Trailing slashes come from hand-typed URLs and pasted links.
  const path = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;

  let best: string | null = null;
  let bestLength = -1;

  for (const item of items) {
    for (const root of [item.to, ...(item.covers ?? [])]) {
      // Home owns only itself; by prefix it would own the entire application.
      const matches = root === '/' ? path === '/' : path === root || path.startsWith(`${root}/`);
      if (matches && root.length > bestLength) {
        best = item.to;
        bestLength = root.length;
      }
    }
  }

  return best;
}
